/**
 * Owner: Haselton Baker Risk Group, LLC
 * Product: SP3 - SP3-Portfolio-UI
 * Developer: Mike McGlone <mike@hbrisk.com>
 * Date: 4/30/21
 * Copyright All Rights Reserved
 */
import { get, getOr, head, filter, pick } from 'lodash/fp';
import { createSelector } from 'reselect';
import pickColor from 'support/pickColor';
import { analysisTypesById } from 'constants/analysisTypes';

const selectAnalysisType = (_, props) => props.analysisType;

const selectReturnPeriod = (_, props) => props.returnPeriod;

const selectConfidenceLevel = (_, props) => props.confidenceLevel;

const selectInventoryId = (_, props) => props.inventoryId;

const selectScenarioId = (_, props) => props.scenarioId;

const selectPerformanceMetric = (_, props) => props.performanceMetric;

export const selectInventoryIds = state => state.entities.inventories.allIds;

export const selectInventoriesById = state => state.entities.inventories.byId;

export const selectInventories = createSelector(
  selectInventoryIds,
  selectInventoriesById,
  (allIds, byId) => allIds.map(uuid => (
    byId[uuid] ? byId[uuid] : ({
      uuid,
    })
  ))
);

export const selectInventoryMenuItems = createSelector(
  selectInventories,
  inventories => inventories.map(item => ({
    id: item.uuid,
    name: item.name,
    value: item.uuid,
  }))
);

export const selectInventoryById = createSelector(
  selectInventoriesById,
  selectInventoryId,
  (byId, id) => byId[id]
);

export const selectInventoryScenarioMenuItems = createSelector(
  selectInventoryById,
  (inventory) => {
    const scenarios = get('scenarios', inventory);
    return scenarios
      ? scenarios.map(item => ({
        id: item.uuid,
        name: item.description,
        value: item.uuid,
      }))
      : [];
  }
);

const selectModelInputsArray = createSelector(
  selectInventoryById,
  inventory => getOr([], 'modelInputs', inventory)
);

const selectModelResultsArray = createSelector(
  selectInventoryById,
  (inventory) => {
    const results = getOr([], 'modelResults', inventory);
    return Array.isArray(results) ? results : Object.values(results);
  }
);

const selectPortfolioResultsArray = createSelector(
  selectInventoryById,
  (inventory) => {
    const results = getOr([], 'portfolioResults', inventory);
    return Array.isArray(results) ? results : Object.values(results);
  }
);

const filterResultsArray = (queryParams, results) => {
  if (!queryParams) {
    return results;
  }
  const paramKeys = Object.keys(queryParams);
  return filter(
    result => (
      paramKeys.every(key => (queryParams[key] === undefined || result[key] === queryParams[key]))
    ),
    results
  );
};

const findInResultsArray = (queryParams, results) => {
  if (!queryParams) {
    return head(results) || null;
  }
  const paramKeys = Object.keys(queryParams);
  const r = results.find(result =>
    (paramKeys.every(key => (queryParams[key] === undefined || result[key] === queryParams[key]))));
  return r || null;
};

export const selectResultsGroupIsCached = createSelector(
  selectAnalysisType,
  selectConfidenceLevel,
  selectPerformanceMetric,
  selectScenarioId,
  selectReturnPeriod,
  selectModelResultsArray,
  (analysisType, confidenceLevel, performanceMetric, scenarioId, returnPeriod, results) => {
    const { searchParams } = analysisTypesById[analysisType];
    const params = pick(
      searchParams,
      {
        confidenceLevel, performanceMetric, scenarioId, returnPeriod,
      }
    );
    return !!findInResultsArray(params, results);
  }
);
export const selectInventoryPortfolioResult = createSelector(
  selectAnalysisType,
  selectConfidenceLevel,
  selectPerformanceMetric,
  selectScenarioId,
  selectReturnPeriod,
  selectPortfolioResultsArray,
  (analysisType, confidenceLevel, performanceMetric, scenarioId, returnPeriod, results) => {
    if (results.length < 1) {
      return null;
    }
    const { searchParams } = analysisTypesById[analysisType];
    const params = pick(
      searchParams,
      {
        confidenceLevel, performanceMetric, scenarioId, returnPeriod,
      }
    );
    const result = findInResultsArray(params, results);
    return getOr(null, 'value', result);
  }
);

export const selectInventoryModelsForMap = createSelector(
  selectAnalysisType,
  selectConfidenceLevel,
  selectPerformanceMetric,
  selectScenarioId,
  selectReturnPeriod,
  selectModelInputsArray,
  selectModelResultsArray,
  (analysisType, confidenceLevel, performanceMetric, scenarioId, returnPeriod, inputs, results) => {
    if (inputs.length < 1) {
      return [];
    }

    const { searchParams } = analysisTypesById[analysisType];
    const params = pick(
      searchParams,
      {
        confidenceLevel, performanceMetric, scenarioId, returnPeriod,
      }
    );
    const filteredModelResults = filterResultsArray(
      params,
      results
    );
    return inputs.reduce(
      (acc, modelInput) => {
        const modelResult = filteredModelResults.find(item => item.modelId === modelInput.uuid);
        const value = modelResult ? modelResult.value : null;
        return [
          ...acc,
          {
            ...modelInput,
            result: {
              value,
            },
            color: pickColor(performanceMetric, value),
          },
        ];
      },
      []
    );
  }
);



// WEBPACK FOOTER //
// ./js/selectors/entities/inventories.js